// DCAComponent.jsx - Updated with system settings
import React, { useEffect } from "react";
import DropdownPair from "../commons/DropdownPair";
import DropdownExchange from "../commons/DropdownExchange";
import InputWithIcon from "../commons/InputWithIcon";
import CustomSlider from "../commons/CustomSlider";
import AvailableForTrade from "./common/AvailableForTrade";
import { useBot, BOT_STRATEGIES } from "../../context/botContext";
import SystemSettings, {
  LoadingState,
  EmptyState,
  SettingsSection,
} from "./SystemSettings";
import { useDCAState } from "../../hooks/useDcaState";
import { getSafeNumericValue } from "../../utils";
import { useBotProfileQuery } from "../../hooks/useBotQueries";
import LabelWithTooltip from "../commons/LabelWithTooltip";

const DCAComponent = ({
  pairs,
  exchanges,
  selectedExchange,
  setSelectedExchange,
  selectedPair,
  setSelectedPair,
  loadingPair,
  loadingExchanges,
  formDCA,
  setFormDCA,
  userBalance,
  errors,
  setErrors,
  loadingBalance,
  actualUserBalance,
  selectedBot,
}) => {
  const { openDialogBot, selectedSetting, setSelectedSetting } = useBot();

  const { data: systemResults, isLoading: loadingGetSetting } =
    useBotProfileQuery(selectedBot?.id, selectedPair?.id);

  const {
    strategy,
    baseCapital,
    activeIndex,
    setActiveIndex,
    capitalInput,
    percentage,
    handleCapitalChange,
    handleCapitalBlur,
    handleSliderChange,
    handleChangeTradeSize,
    setSystemSettingData,
  } = useDCAState({
    userBalance,
    selectedBot,
    formDCA,
    setFormDCA,
    setSelectedExchange,
    selectedExchange,
    setSelectedPair,
    selectedPair,
    openDialogBot,
    selectedSetting,
    setSelectedSetting,
    setErrors,
    errors,
    actualUserBalance,
  });

  const isEdit = openDialogBot === "step-bot-2-edit";
  const capitalError = errors?.capital;

  const handleSystemSettingClick = (setting) => {
    setActiveIndex(setting.key);
    let formData = { ...(formDCA || {}) };

    const profiles = systemResults?.[setting?.key];
    if (!formData || !Array.isArray(profiles) || profiles.length === 0) return;

    const profile = profiles[0];
    const params = profile?.parameters || [];

    const updatedSettings = { ...(formData.settings || {}) };

    params.forEach(({ parameter_key, parameter_value }) => {
      updatedSettings[parameter_key] = parameter_value;
    });

    // Store original system data
    let originDataSystem = { settings: { ...updatedSettings } };
    setSystemSettingData(originDataSystem);

    // Maintain capital value
    updatedSettings.capital = formData?.settings?.capital;

    const updatedForm = { ...formData, settings: updatedSettings };
    setFormDCA(updatedForm);
  };

  const handleDropdownChange = (setter) => (val) => {
    setter(val);
    if (setter === setSelectedExchange) {
      setSelectedPair(null);
    }
  };

  useEffect(() => {
    const numericCapital = Number(formDCA?.settings?.capital) || 0;
    const numericTradeSize = Number(formDCA?.settings?.amount) || 0;

    // Only show validation when both have values
    // if (numericTradeSize > numericCapital && numericCapital > 0) {
    //   // setErrors((prev) => ({
    //   //   ...prev,
    //   //   amount: "Trade size cannot exceed capital.",
    //   // }));
    //   setFormDCA((prev) => ({
    //     ...prev,
    //     settings: { ...prev.settings, amount: numericCapital },
    //   }));
    // } else {
    //   // Clear the error when valid again
    //   setErrors((prev) => {
    //     const { amount, ...rest } = prev || {};
    //     return rest;
    //   });
    // }
    setErrors((prev) => {
      const { amount, ...rest } = prev || {};
      return rest;
    });
  }, [formDCA?.settings?.capital, formDCA?.settings?.amount, setErrors]);

  const settingsFields = [
    {
      name: "amount",
      label: "Trade Size",
      placeholder: "Trade Size",
      rightIcon: selectedPair?.base_currency || "USDT",
      explanation: "Total capital the bot can use.",
    },
    {
      name: "max_order_count",
      label: "Maximum order",
      placeholder: "Maximum order",
      rightIcon: "orders",
      explanation: "Limit on total DCA buys.",
    },
    {
      name: "price_change_percentage",
      label: "Price change",
      placeholder: "Price change",
      rightIcon: "%",
      explanation: "Price move needed to place next buy.",
    },
    {
      name: "take_profit_percentage",
      label: "Take profit (buy back)",
      placeholder: "Take profit (buy back)",
      rightIcon: "%",
      explanation: "Profit level to secure gains.",
    },
    {
      name: "trigger_price",
      label: "Trigger price",
      placeholder: "Trigger price",
      rightIcon: selectedPair?.base_currency || "USDT",
      explanation: "Price where DCA starts.",
    },
  ];

  return (
    <>
      <DropdownExchange
        id="exchange"
        value={selectedExchange}
        onChange={handleDropdownChange(setSelectedExchange)}
        options={exchanges}
        loading={loadingExchanges}
        disabled={!!formDCA?.exchange_name}
        placeholder={formDCA?.exchange_name || "Select Exchange"}
      />

      <DropdownPair
        id="pair"
        value={selectedPair}
        onChange={handleDropdownChange(setSelectedPair)}
        options={pairs}
        loading={loadingPair}
        disabled={!!formDCA?.coin_name}
        placeholder={formDCA?.coin_name || "Select Pair"}
      />

      <div className="mt-4">
        <AvailableForTrade
          userBalance={actualUserBalance}
          loadingBalance={loadingBalance}
          selectedExchange={selectedExchange}
          selectedPair={selectedPair}
          exchangeName={formDCA?.exchange_name}
          coinName={formDCA?.coin_name}
        />
      </div>

      <div className="space-y-2 !mb-5">
        <span className="text-[#697077] text-sm">Capital allocation</span>
        <InputWithIcon
          value={capitalInput}
          onChange={handleCapitalChange}
          placeholder="Capital Allocation"
          rightIcon={selectedPair?.base_currency || "USDT"}
          inputClassName="text-right [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:m-0 [appearance:textfield]"
          type="text"
          inputMode="decimal"
        />
        <div className="!mt-4">
          <CustomSlider
            percentage={percentage}
            setPercentage={handleSliderChange}
          />
        </div>
        {capitalError && (
          <p className="text-red-500 text-sm mt-1.5">{capitalError}</p>
        )}
      </div>

      <div className="border border-gray-300 mt-5 rounded-[10px] cursor-pointer relative overflow-hidden divide-y">
        <div className="bg-[#F2F4F8] p-3">
          <span className="text-black block">Settings</span>
          <span className="block text-[#697077] capitalize">
            Setting Your {strategy} Bot
          </span>
        </div>

        {openDialogBot !== "step-bot-2-edit" && (
          <SettingsSection
            title="By System"
            settingKey="system"
            selectedSetting={selectedSetting}
            setSelectedSetting={setSelectedSetting}
          >
            {loadingGetSetting ? (
              <LoadingState message="Loading system data..." />
            ) : !systemResults || Object.keys(systemResults).length === 0 ? (
              <EmptyState onLinkClick={() => setSelectedSetting("custom")} />
            ) : (
              <SystemSettings
                strategy={strategy}
                settings={systemResults}
                activeIndex={activeIndex}
                onSettingClick={handleSystemSettingClick}
                excludeKeys={["capital"]} // Exclude capital from system settings display
              />
            )}
          </SettingsSection>
        )}

        <SettingsSection
          title="Customize Own"
          settingKey="custom"
          selectedSetting={selectedSetting}
          setSelectedSetting={setSelectedSetting}
        >
          <div className="p-3 grid gap-4 grid-cols-2 mt-3">
            {settingsFields.map((field) => {
              const fieldError = errors?.[field.name];

              return (
                <div
                  key={field.name}
                  className={field.name === "trigger_price" ? "col-span-2" : ""}
                >
                  <LabelWithTooltip
                    label={field.label}
                    explanation={field.explanation}
                  />
                  <InputWithIcon
                    id={field.name}
                    name={field.name}
                    value={formDCA?.settings?.[field.name]}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9.]/g, "");
                      const name = field.name;

                      setFormDCA((prev) => ({
                        ...prev,
                        settings: {
                          ...(prev?.settings || {}),
                          [name]: value,
                        },
                      }));
                    }}
                    placeholder={field.placeholder}
                    rightIcon={field.rightIcon}
                    type="text"
                    inputMode="decimal"
                    inputClassName="[&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:m-0 [appearance:textfield]"
                    disabled={!!isEdit}
                  />
                  {fieldError && (
                    <p className="text-red-500 text-sm mt-1.5">{fieldError}</p>
                  )}
                </div>
              );
            })}
          </div>
        </SettingsSection>
      </div>
    </>
  );
};

export default DCAComponent;
